import React from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    TextField,
    Badge,
} from '@mui/material';
import {
    Close,
    Delete,
    ShoppingCart,
    Add,
    Remove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const CartDrawer = () => {
    const navigate = useNavigate();
    const {
        cartItems,
        isOpen,
        setIsOpen,
        removeFromCart,
        updateQuantity,
        getCartTotal,
        getCartCount,
    } = useCart();

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleCheckout = () => {
        handleClose();
        navigate('/checkout');
    };

    const handleViewCart = () => {
        handleClose();
        navigate('/cart');
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 400 } },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingCart color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Shopping Cart
                        </Typography>
                        <Badge badgeContent={getCartCount()} color="primary" />
                    </Box>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Cart Items */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                    {cartItems.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                gap: 2,
                            }}
                        >
                            <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="h6" color="text.secondary">
                                Your cart is empty
                            </Typography>
                            <Button variant="contained" onClick={handleClose}>
                                Continue Shopping
                            </Button>
                        </Box>
                    ) : (
                        <List>
                            {cartItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    <ListItem
                                        alignItems="flex-start"
                                        sx={{
                                            flexDirection: 'column',
                                            gap: 1,
                                            p: 2,
                                            bgcolor: 'grey.50',
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant="rounded"
                                                    src={item.thumbnailUrl || item.imageUrl}
                                                    sx={{ width: 60, height: 60 }}
                                                >
                                                    <ShoppingCart />
                                                </Avatar>
                                            </ListItemAvatar>

                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="bold"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                            }}
                                                        >
                                                            {item.displayName || item.partNumber || item.mpn}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.manufacturer || 'Unknown'}
                                                        </Typography>
                                                    }
                                                />

                                                <Typography variant="body2" color="primary" fontWeight="bold">
                                                    {item.formattedPrice ||
                                                        (item.unitPrice
                                                            ? `$${item.unitPrice.toFixed(2)}`
                                                            : item.price
                                                                ? `$${item.price.toFixed(2)}`
                                                                : 'N/A')}
                                                </Typography>
                                            </Box>

                                            <IconButton
                                                size="small"
                                                onClick={() => removeFromCart(item.id)}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>

                                        {/* Quantity Controls */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography variant="body2" sx={{ minWidth: 60 }}>Quantity:</Typography>
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center',
                                                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                                            }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                    disabled={item.quantity <= (item.minimumOrderQuantity || 1)}
                                                >
                                                    <Remove fontSize="small" />
                                                </IconButton>
                                                <TextField
                                                    size="small"
                                                    value={item.quantity}
                                                    onChange={(e) => {
                                                        const minQty = item.minimumOrderQuantity || 1;
                                                        const value = parseInt(e.target.value) || minQty;
                                                        updateQuantity(item.id, value);
                                                    }}
                                                    inputProps={{
                                                        min: item.minimumOrderQuantity || 1,
                                                        style: { textAlign: 'center', width: 40 },
                                                    }}
                                                    sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } } }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                >
                                                    <Add fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            {(item.minimumOrderQuantity > 1) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Min: {item.minimumOrderQuantity}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                                Subtotal:{' '}
                                                <strong>
                                                    ${((item.unitPrice || item.price || 0) * item.quantity).toFixed(2)}
                                                </strong>
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Footer */}
                {cartItems.length > 0 && (
                    <Box
                        sx={{
                            p: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Subtotal ({getCartCount()} items)
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    ${getCartTotal().toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Shipping
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Calculated at checkout
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6" fontWeight="bold">
                                    Total
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                    ${getCartTotal().toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleCheckout}
                            sx={{ mb: 1 }}
                        >
                            Proceed to Checkout
                        </Button>
                        <Button variant="outlined" fullWidth onClick={handleViewCart}>
                            View Cart
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

export default CartDrawer;
